import { UserRole } from '../user';
import { EventColor } from './colors';
import { getEventColor, INTERNAL_EVENT_COLOR } from './colors';
import { EventIcon } from './icons';

export * from './colors';
export * from './icons';

export interface EventTarget {
    groupIds?: string[];
    consumerIds?: string[];
    sendToAll?: boolean;
}

export interface EventAudienceCounts {
    residentCount: number;
    relativeCount: number;
    mainContactCount: number;
}

export enum EventRecurrenceFrequency {
    daily = 'daily',
    weekly = 'weekly',
    monthly = 'monthly',
}

export enum EventType {
    residents = 'residents',
    internal = 'internal',
    group = 'group',
    relatives = 'relatives',
}

export enum EventStatus {
    active = 'active',
    cancelled = 'cancelled',
}

export const getMunduEventType = (
    event: Pick<MunduEvent, 'type' | 'internal'> & { target?: EventTarget },
): EventType => {
    if (event.type !== undefined && event.type !== null) return event.type;

    const type =
        event.type ??
        (event.internal ? EventType.internal : EventType.residents);

    // Infer an audience-based type only for legacy events without a type.
    if (
        type === EventType.residents &&
        !event.target?.sendToAll &&
        (event.target?.groupIds?.length || event.target?.consumerIds?.length)
    ) {
        return EventType.group;
    }

    return type;
};

export const getEventTypeColor = (type: EventType) => {
    switch (type) {
        case EventType.internal:
            return INTERNAL_EVENT_COLOR;
        case EventType.relatives:
            return getEventColor(EventColor.blue);
        case EventType.group:
            return getEventColor(EventColor.terracotta);
        case EventType.residents:
        default:
            return getEventColor(EventColor.green);
    }
};

export const getEventTypeStoredColor = (type: EventType): EventColor => {
    switch (type) {
        case EventType.relatives:
            return EventColor.blue;
        case EventType.internal:
            return EventColor.gold;
        case EventType.group:
            return EventColor.terracotta;
        case EventType.residents:
        default:
            return EventColor.green;
    }
};

export const isResidentActivity = (event: MunduEvent): boolean => {
    const type = getMunduEventType(event);
    return type === EventType.residents || type === EventType.group;
};

export interface EventRecurrence {
    frequency: EventRecurrenceFrequency;
    interval: number;
    until: string;
    timeZone: string;
}

export type RsvpStatus =
    | 'going'
    | 'notGoing'
    | 'waitingForReply'
    | 'noResponse';

export interface EventResponseStatusRow {
    inviteeId: string;
    name?: string;
    avatarUrl?: string;
    hasEverLoggedIn?: boolean;
    lastLoggedInAt?: string;
    residents: { id: string; name?: string }[];
    status: RsvpStatus;
    respondedAt?: string;
    latestReminder?: {
        attemptedAt: string;
        result: 'sent' | 'failed';
        failureCategory?: string;
    };
}

export interface RsvpRosterCounts {
    totalInvited: number;
    going: number;
    notGoing: number;
    waitingForReply: number;
    noResponse: number;
}

/** Organizer-only Response status for one event occurrence. */
export interface EventResponseStatus {
    eventId: string;
    occurrenceId?: string;
    deadline: string;
    /** True once the deadline passed and the roster is frozen. */
    closed: boolean;
    canSendResponseReminder: boolean;
    canRetryFailedResponseReminders: boolean;
    reminderEligibleCount: number;
    retryEligibleCount: number;
    reminderCounts: { sent: number; failed: number };
    counts: RsvpRosterCounts;
    rows: EventResponseStatusRow[];
}

export interface MunduEvent {
    canViewResponseStatus?: boolean;
    canConfigureRequireResponse?: boolean;
    hasRsvpHistory?: boolean;
    requireResponse?: boolean;
    timeZone?: string;
    id: string;
    organizationId: string;
    organizationDisplayName?: string;
    createdById: string;
    createdByRole: UserRole;
    createdByName?: string;
    createdByAvatarUrl?: string;
    title: string;
    description?: string;
    location?: string;
    startDate: string;
    endDate: string;
    allDay?: boolean;
    internal?: boolean;
    status?: EventStatus;
    cancelledAt?: string;
    cancelledById?: string;
    type?: EventType;
    color?: EventColor;
    icon?: EventIcon;
    recurrence?: EventRecurrence;
    occurrenceId?: string;
    originalOccurrenceStartDate?: string;
    hasOccurrenceExceptions?: boolean;
    isOccurrenceCancelled?: boolean;
    seriesStartDate?: string;
    seriesEndDate?: string;
    target: EventTarget;
    recipientIds: string[];
    recipientCount: number;
    residentCount?: number;
    relativeCount?: number;
    mainContactCount?: number;
    createdDate: string;
    updatedDate?: string;
}
